import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { initializeApp } from 'firebase/app';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module'; 
import { AppComponent } from './app.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { TpoChangePasswordComponent } from './components/change-password/tpo-change-password.component';
import { CompanySwitchComponent } from './components/company-switch/company-switch.component';
import { ESignViaTokenConfirmationComponent } from './components/e-sign-via-token-confirmation/e-sign-via-token-confirmation.component';
import { ESignVendorConfirmationComponent } from './components/e-sign-vendor-confirmation/e-sign-vendor-confirmation.component';
import { ESignViaTokenComponent } from './components/e-sign-via-token/e-sign-via-token.component';
import { EmailUnsubscribeComponent } from './components/email-unsubscribe/email-unsubscribe.component';
import { ErrorComponent } from './components/error/error.component';
import { ExternalAuthCallbackComponent } from './components/ext-auth-callback/ext-auth-callback.component';
/*
 * Legacy/unused now that SignNow authenticates with a per-company API key set by super admins.
 * The callback route is disabled and unreachable; the component files remain in the tree so this import and
 * its declaration below can be restored by uncommenting these places.
 * import { SignNowOauthCallbackComponent } from './components/signnow-oauth-callback/signnow-oauth-callback.component';
 */
import { SamlCallbackComponent } from './components/saml-callback/saml-callback.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { LoginCompanyPickerDialogComponent } from './components/login-company-picker-dialog/login-company-picker-dialog.component';
import { LoginComponent } from './components/login/login.component';
import { MessagingReportMessagePreviewComponent } from './components/messaging-report-message-preview/messaging-report-message-preview.component';
import { MicrosoftLoginRedirectComponent } from './components/microsoft-login-redirect/microsoft-login-redirect.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { TvAuthComponent } from './components/tv-auth/tv-auth.component';
import { DeviceAuthorizeComponent } from './components/device-authorize/device-authorize.component';
import { AccountPausedComponent } from './components/account-paused/account-paused.component';
import { SessionTimeoutSyncComponent } from './components/session-timeout/session-timeout-sync/session-timeout-sync.component';
import { SessionTimeoutComponent } from './components/session-timeout/session-timeout.component';
import { ShortLinkRedirectComponent } from './components/short-link-redirect/short-link-redirect.component';
import { CoreModule } from './core/core.module';
import { DataService } from './core/services/data.service';
import { DocumentService } from './core/services/document.service';
import { provideNgbOverlayDefaults } from './core/ngb-overlay-config';
import { httpInterceptorProviders } from './core/services/http-interceptors';
import { LocalStorageService } from './core/services/local-storage.service';
import { LayoutModule } from './layout/layout.module';
import { AgentTypeService } from './modules/admin/agent-config/services/agent-type-service';
import { AlignmentService } from './modules/admin/company/services/alignment.service';
import { CompanyUsersService } from './modules/admin/company/services/company-users.service';
import { RolesService } from './modules/admin/company/services/roles.service';
import { EmailCampaignConfigService } from './modules/admin/email-campaign-config/services/email-campaign-config.service';
import { EmailPreferenceCenterService } from './modules/admin/email-campaign-config/services/email-preference-center.service';
import { TagManagementService } from './modules/admin/tag-management/services/tag-management.service';
import { TpoConfigService } from './modules/admin/tpo-config/services/tpo-config.service';
import { AmortizationModule } from './modules/amortization/amortization.module';
import { BorrowerModule } from './modules/borrower/borrower.module';
import { ChangeOfCircumstanceModule } from './modules/change-of-circumstance/change-of-circumstance.module';
import { ConditionsTasksModule } from './modules/conditions-tasks/conditions-tasks.module';
import { ContactsService } from './modules/contacts/services/contacts.service';
import { CorrespondenceModule } from './modules/correspondence/correspondence.module';
import { DashboardV2Service } from './modules/dashboardv2/services/dashboardv2.service';
import { DialerService } from './modules/dialer/services/dialer.service';
import {
  DocumentESigningRequestConfirmationComponent,
} from './modules/document-signing/document-esigning-request-confirmation-component/document-esigning-request-confirmation-component';
import {
  DocumentSigningRequestConfirmationComponent,
} from './modules/document-signing/document-signing-request-confirmation/document-signing-request-confirmation.component';
import { ExpressionBuilderModule } from './modules/expression-builder/expression-builder.module';
import { FeesModule } from './modules/fees/fees.module';
import { MortgageCtoFeeWizardModule } from './modules/fees/mortgage-cto-fee-wizard/mortgage-cto-fee-wizard.module';
import { FileContactsModule } from './modules/file-contacts/file-contacts.module';
import { InvoicingModule } from './modules/invoicing/invoicing.module';
import { LeadsService } from './modules/leads/services/leads.service';
import { DocsService } from './modules/loan-docs/services/docs.service';
import { NextGenLayoutModule } from './modules/loda-nextgen/layout/nextgen-layout.module';
import { NewApplicationModule } from './modules/new-application/new-application.module';
import { PricingModule } from './modules/pricing/pricing.module';
import { TpoModule } from './modules/tpo/tpo.module';
import { UWDecisionModule } from './modules/uw-decision/uw-decision.module';
import { AgentService } from './services/agent.service';
import { AuthService } from './services/auth.service';
import { CalendarDaysService } from './services/calendar-days.service';
import { ChannelService } from './services/channel.service';
import { ChatService } from './services/chat.service';
import { CheckListService } from './services/check-list.service';
import { ContactListService } from './services/contact-list.service';
import { DocumentTemplateService } from './services/document-template.service';
import { EmailService } from './services/email.service';
import { EnumerationService } from './services/enumeration-service';
import { FirebaseMessageService, FirebaseService, FirebaseServicesService } from './services/firebase';
import { GlobalConfigService } from './services/global-config.service';
import { LeadRouteService } from './services/lead-route.service';
import { LoanService } from './services/loan';
import { LogService } from './services/log.service';
import { LosService } from './services/los.service';
import { MentionsService } from './services/mentions.service';
import { MenuService } from './services/menu.service';
import { MergeFieldsService } from './services/merge-fields.service';
import { MiQuoteService } from './services/mi-quote.service';
import { MortgageCallReportService } from './services/mortgage-call-report.service';
import { MortgageService } from './services/mortgage.service';
import { NavigationService } from './services/navigation.service';
import { NotificationService } from './services/notification.service';
import { PipelineService } from './services/pipeline.service';
import { ProductService } from './services/product.service';
import { SignalRService } from './services/signalr.service';
import { StoredFileService } from './services/stored-file.service';
import { SystemLevelService } from './services/system-level.service';
import { ThirdPartyCredentialsService } from './services/third-party-credentials.service';
import { TitleService } from './services/title.service';
import { UserService } from './services/user.service';
import { ZipCodeService } from './services/zipcode.service';
import { SharedModule } from './shared/shared.module';
import { providePrimeNG } from 'primeng/config';
import MyPreset from './my-preset';
import { MarkdownModule } from 'ngx-markdown';

const firebaseConfig = environment.firebase;
delete firebaseConfig.vapidKey
initializeApp(firebaseConfig);

const MODULES = [LayoutModule, ExpressionBuilderModule, NextGenLayoutModule];

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ExternalAuthCallbackComponent,
    /*
     * Legacy/unused now that SignNow authenticates with a per-company API key set by super admins.
     * The callback route is disabled and unreachable; uncomment this declaration and the import above to restore it.
     * SignNowOauthCallbackComponent,
     */
    SamlCallbackComponent,
    CompanySwitchComponent,
    LoginCompanyPickerDialogComponent,
    ForgotPasswordComponent,
    ChangePasswordComponent,
    TpoChangePasswordComponent,
    ResetPasswordComponent,
    DocumentSigningRequestConfirmationComponent,
    EmailUnsubscribeComponent,
    ErrorComponent,
    DocumentESigningRequestConfirmationComponent,
    MicrosoftLoginRedirectComponent,
    SessionTimeoutComponent,
    SessionTimeoutSyncComponent,
    ShortLinkRedirectComponent,
    ESignViaTokenComponent,
    ESignViaTokenConfirmationComponent,
    ESignVendorConfirmationComponent,
    MessagingReportMessagePreviewComponent,
    TvAuthComponent,
    DeviceAuthorizeComponent,
    AccountPausedComponent
  ],
  imports: [
    ReactiveFormsModule,
    CoreModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    TpoModule,
    NewApplicationModule,
    RouterModule,
    PricingModule,
    FeesModule,
    AmortizationModule,
    ConditionsTasksModule,
    ChangeOfCircumstanceModule,
    UWDecisionModule,
    BorrowerModule,
    FileContactsModule,
    InvoicingModule,
    ...MODULES,
    MortgageCtoFeeWizardModule,
      CorrespondenceModule,
      MarkdownModule.forRoot()
    ],
  providers: [
    LoanService,
    UserService,
    AuthService,
    LocalStorageService,
    NavigationService,
    MenuService,
    GlobalConfigService,
    DataService,
    EnumerationService,
    MortgageService,
    PipelineService,
    SystemLevelService,
    RolesService,
    NotificationService,
    AgentTypeService,
    CompanyUsersService,
    LosService,
    AlignmentService,
    ChannelService,
    CheckListService,
    ContactListService,
    DocumentService,
    httpInterceptorProviders,
    AgentService,
    TagManagementService,
    ContactsService,
    LeadsService,
    ZipCodeService,
    DocumentTemplateService,
    EmailService,
    ChatService,
    DialerService,
    LogService,
    SignalRService,
    FirebaseMessageService,
    FirebaseServicesService,
    FirebaseService,
    LeadRouteService,
    MentionsService,
    ProductService,
    EmailCampaignConfigService,
    EmailPreferenceCenterService,
    MortgageCallReportService,
    MergeFieldsService,
    TitleService,
    TpoConfigService,
    CurrencyPipe,
    PercentPipe,
    DecimalPipe,
    ThirdPartyCredentialsService,
    StoredFileService,
    CalendarDaysService,
    DashboardV2Service,
    MiQuoteService,
    DocsService,
    { provide: Window, useValue: window },
    provideNgbOverlayDefaults(),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: MyPreset,
        options: {
          prefix: 'p',
          // The same attribute Bootstrap's color modes and the app's color tokens key off (set by ThemeService).
          darkModeSelector: '[data-bs-theme="dark"]',
          cssLayer: false
        }
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
